import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Chip,
  Divider,
  Tabs,
  Tab,
  Button,
  Grid,
  Paper,
  Avatar,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CloseOutlined,
  EmailOutlined,
  PhoneOutlined,
  BusinessOutlined,
  LocationOnOutlined,
  LanguageOutlined,
  PersonOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  TrendingUpOutlined,
  AttachMoneyOutlined,
  CalendarTodayOutlined,
  StarOutlined,
  PersonAddOutlined,
  VerifiedOutlined,
  AutoAwesomeOutlined,
} from '@mui/icons-material';
import AIScoreBadge from './AIScoreBadge';
import ActivityTimeline, { Activity } from './ActivityTimeline';
import AddNoteForm, { AddNoteFormData } from './AddNoteForm';
import { formatDistanceToNow } from 'date-fns';
import zhTW from 'date-fns/locale/zh-TW';

export interface Lead {
  id: string;
  leadNumber: string;
  name: string;
  email?: string;
  phone?: string;
  companyName?: string;
  position?: string;
  source: string;
  status: string;
  priority: string;
  aiScore: number;
  scoringFactors?: {
    engagement: number;
    budget: number;
    authority: number;
    need: number;
    timing: number;
  };
  estimatedBudget?: number;
  lastContactedAt?: Date | string;
  aiSuggestion?: string;
  industry?: string;
  location?: string;
  website?: string;
  employeeCount?: string;
  tags?: string[];
  assignedUser?: {
    id: string;
    name: string;
    email?: string;
  };
  notes?: string;
  emailVerified?: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface StaffAssignee {
  id: string;
  name: string;
  email: string;
  role?: string;
}

interface LeadDetailDrawerProps {
  open: boolean;
  lead: Lead | null;
  activities?: Activity[];
  assignees?: StaffAssignee[];
  onClose: () => void;
  onUpdateStatus?: (leadId: string, newStatus: string) => void | Promise<void>;
  onUpdatePriority?: (leadId: string, newPriority: string) => void | Promise<void>;
  onAddActivity?: (leadId: string, activity: AddNoteFormData) => void | Promise<void>;
  onAssignLead?: (leadId: string, assignedToUserId: string) => void | Promise<void>;
  onEdit?: (lead: Lead) => void;
  onDelete?: (leadId: string) => void;
  onSendEmail?: (lead: Lead) => void;
  onVerifyEmail?: (lead: Lead) => void;
  onConvert?: (leadId: string) => void;
  onScoreLead?: (leadId: string) => void | Promise<void>;
  isUpdating?: boolean;
  isAssigning?: boolean;
  isScoring?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`lead-detail-tabpanel-${index}`}
      aria-labelledby={`lead-detail-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const statusOptions = [
  { value: 'NEW', label: 'New 新增', color: 'primary' },
  { value: 'CONTACTED', label: 'Contacted 已聯繫', color: 'default' },
  { value: 'QUALIFIED', label: 'Qualified 合格', color: 'success' },
  { value: 'UNQUALIFIED', label: 'Unqualified 不合格', color: 'error' },
  { value: 'CONVERTED', label: 'Converted 已轉換', color: 'success' },
  { value: 'LOST', label: 'Lost 已流失', color: 'error' },
] as const;

const priorityOptions = [
  { value: 'URGENT', label: 'Urgent 緊急', color: '#ff4d4f' },
  { value: 'HIGH', label: 'High 高', color: '#faad14' },
  { value: 'MEDIUM', label: 'Medium 中', color: '#1890ff' },
  { value: 'LOW', label: 'Low 低', color: '#52c41a' },
] as const;

const LeadDetailDrawer: React.FC<LeadDetailDrawerProps> = ({
  open,
  lead,
  activities = [],
  assignees = [],
  onClose,
  onUpdateStatus,
  onUpdatePriority,
  onAddActivity,
  onAssignLead,
  onEdit,
  onDelete,
  onSendEmail,
  onVerifyEmail,
  onConvert,
  onScoreLead,
  isUpdating = false,
  isAssigning = false,
  isScoring = false,
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [isAddingActivity, setIsAddingActivity] = useState(false);

  if (!lead) return null;

  const handleStatusChange = async (newStatus: string) => {
    if (onUpdateStatus) {
      await onUpdateStatus(lead.id, newStatus);
    }
  };

  const handlePriorityChange = async (newPriority: string) => {
    if (onUpdatePriority) {
      await onUpdatePriority(lead.id, newPriority);
    }
  };

  const handleAssignLead = async (assignedToUserId: string) => {
    if (onAssignLead && assignedToUserId) {
      await onAssignLead(lead.id, assignedToUserId);
    }
  };

  const handleAddActivity = async (data: AddNoteFormData) => {
    if (onAddActivity) {
      await onAddActivity(lead.id, data);
      setIsAddingActivity(false);
    }
  };

  const getStatusColor = (status: string) => {
    return statusOptions.find(s => s.value === status)?.color || 'default';
  };

  const getPriorityColor = (priority: string) => {
    return priorityOptions.find(p => p.value === priority)?.color || '#1890ff';
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90%', md: 700 },
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'grey.50',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {lead.leadNumber}
                </Typography>
                <Chip
                  label={statusOptions.find(s => s.value === lead.status)?.label || lead.status}
                  size="small"
                  color={getStatusColor(lead.status) as any}
                />
                <Chip
                  label={priorityOptions.find(p => p.value === lead.priority)?.label || lead.priority}
                  size="small"
                  sx={{
                    backgroundColor: getPriorityColor(lead.priority),
                    color: 'white',
                  }}
                />
              </Stack>

              <Typography variant="h5" gutterBottom>
                {lead.name}
              </Typography>

              {lead.companyName && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <BusinessOutlined fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {lead.companyName}
                    {lead.position && ` • ${lead.position}`}
                  </Typography>
                </Stack>
              )}

              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {lead.email && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <EmailOutlined fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {lead.email}
                    </Typography>
                    {lead.emailVerified ? (
                      <Tooltip title="Email verified">
                        <VerifiedOutlined sx={{ fontSize: 14, color: 'success.main' }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Click to verify this email is correct">
                        <IconButton
                          size="small"
                          onClick={() => onVerifyEmail?.(lead)}
                          sx={{ p: 0.25 }}
                        >
                          <VerifiedOutlined sx={{ fontSize: 14, color: 'grey.400', '&:hover': { color: 'success.main' } }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                )}
                {lead.phone && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PhoneOutlined fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {lead.phone}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>

            <IconButton onClick={onClose} size="small">
              <CloseOutlined />
            </IconButton>
          </Stack>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            {onSendEmail && lead.email && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EmailOutlined />}
                onClick={() => onSendEmail(lead)}
              >
                Email
              </Button>
            )}
            {lead.phone && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhoneOutlined />}
                onClick={() => window.open(`tel:${lead.phone}`)}
              >
                Call
              </Button>
            )}
            {onEdit && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={() => onEdit(lead)}
              >
                Edit 編輯
              </Button>
            )}
            {onConvert && lead.status !== 'CONVERTED' && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlined />}
                onClick={() => onConvert(lead.id)}
              >
                Convert to Client
              </Button>
            )}
            {onDelete && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlined />}
                onClick={() => onDelete(lead.id)}
              >
                Delete 刪除
              </Button>
            )}
          </Stack>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
            <Tab label="Details 詳情" />
            <Tab label="Activities 活動記錄" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* Details Tab */}
          <TabPanel value={currentTab} index={0}>
            <Stack spacing={3}>
              {/* AI Score Section */}
              <Paper elevation={1} sx={{ p: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <AIScoreBadge
                    score={lead.aiScore}
                    factors={lead.scoringFactors}
                    size="large"
                    variant="detailed"
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      AI Score 評分
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lead.aiScore >= 80 ? 'High potential lead' : lead.aiScore >= 60 ? 'Medium potential' : 'Low potential'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Quick Assign Section */}
              {onAssignLead && assignees.length > 0 && (
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    backgroundColor: !lead.assignedUser ? 'warning.light' : 'grey.50',
                    border: !lead.assignedUser ? '1px solid' : 'none',
                    borderColor: 'warning.main'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <PersonAddOutlined color={!lead.assignedUser ? 'warning' : 'action'} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {!lead.assignedUser ? 'Unassigned Lead - Assign Now!' : 'Assigned To'}
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={lead.assignedUser?.id || ''}
                          displayEmpty
                          onChange={(e) => handleAssignLead(e.target.value)}
                          disabled={isAssigning}
                          startAdornment={isAssigning ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                        >
                          <MenuItem value="" disabled>
                            <em>Select Staff Member</em>
                          </MenuItem>
                          {assignees.map((staff) => (
                            <MenuItem key={staff.id} value={staff.id}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                  {staff.name.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2">{staff.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {staff.email}
                                  </Typography>
                                </Box>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Stack>
                </Paper>
              )}

              {/* Status & Priority */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status 狀態</InputLabel>
                    <Select
                      value={lead.status}
                      label="Status 狀態"
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={isUpdating || !onUpdateStatus}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Priority 優先級</InputLabel>
                    <Select
                      value={lead.priority}
                      label="Priority 優先級"
                      onChange={(e) => handlePriorityChange(e.target.value)}
                      disabled={isUpdating || !onUpdatePriority}
                    >
                      {priorityOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: option.color,
                              }}
                            />
                            <Typography>{option.label}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Lead Information */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Lead Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  {lead.industry && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BusinessOutlined fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Industry 行業
                          </Typography>
                          <Typography variant="body2">{lead.industry}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}

                  {lead.location && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationOnOutlined fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Location 地點
                          </Typography>
                          <Typography variant="body2">{lead.location}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}

                  {lead.website && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LanguageOutlined fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Website 網站
                          </Typography>
                          <Typography
                            variant="body2"
                            component="a"
                            href={lead.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'primary.main', textDecoration: 'none' }}
                          >
                            {lead.website}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}

                  {lead.employeeCount && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonOutlined fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Employees 員工數
                          </Typography>
                          <Typography variant="body2">{lead.employeeCount}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}

                  {lead.estimatedBudget && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AttachMoneyOutlined fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Estimated Budget 預估預算
                          </Typography>
                          <Typography variant="body2">
                            ${lead.estimatedBudget.toLocaleString()}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}

                  {lead.lastContactedAt && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarTodayOutlined fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Last Contacted 最後聯繫
                          </Typography>
                          <Typography variant="body2">
                            {formatDistanceToNow(new Date(lead.lastContactedAt), {
                              addSuffix: true,
                              locale: zhTW,
                            })}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}

                  {lead.source && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StarOutlined fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Source 來源
                          </Typography>
                          <Typography variant="body2">{lead.source}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}

                  {lead.assignedUser && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {lead.assignedUser.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Assigned To 負責人
                          </Typography>
                          <Typography variant="body2">{lead.assignedUser.name}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Tags */}
              {lead.tags && lead.tags.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Tags 標籤
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {lead.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Notes */}
              {lead.notes && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Notes 備註
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {lead.notes}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Stack>
          </TabPanel>

          {/* Activities Tab */}
          <TabPanel value={currentTab} index={1}>
            <Stack spacing={2}>
              {!isAddingActivity && onSendEmail && lead.email && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EmailOutlined />}
                  onClick={() => onSendEmail(lead)}
                  fullWidth
                >
                  Send Email 發送電郵
                </Button>
              )}

              {!isAddingActivity && onAddActivity && (
                <Button
                  variant="outlined"
                  onClick={() => setIsAddingActivity(true)}
                  fullWidth
                >
                  Add Activity 新增活動
                </Button>
              )}

              {isAddingActivity && onAddActivity && (
                <AddNoteForm
                  onSubmit={handleAddActivity}
                  onCancel={() => setIsAddingActivity(false)}
                  compact
                  recipientContext={{
                    name: lead.name,
                    company: lead.companyName,
                    email: lead.email,
                  }}
                />
              )}

              <ActivityTimeline
                activities={activities}
                maxHeight={500}
                emptyMessage="No activities yet. Start by adding a note! 尚無活動記錄，開始新增備註吧！"
              />
            </Stack>
          </TabPanel>

        </Box>
      </Box>
    </Drawer>
  );
};

export default LeadDetailDrawer;
